import { sanitizeDiagnosticText } from "@/lib/aqua/diagnostics";
import type { AquaControlConsequence, AquaScreenContext, AquaVisibleControl, AquaWritableField } from "@/lib/aqua/types";

function isVisible(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  return rect.width > 0 && rect.height > 0 && style.visibility !== "hidden" && style.display !== "none";
}

export function activeScrollContainer(): HTMLElement {
  const registered = Array.from(document.querySelectorAll<HTMLElement>("[data-aqua-scroll]")).find((element) => isVisible(element) && element.scrollHeight > element.clientHeight + 2);
  return registered ?? (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

export function readLiveScreenState(): Pick<AquaScreenContext, "visibleControls" | "writableFields" | "viewport"> {
  if (typeof document === "undefined") {
    return { visibleControls: [], writableFields: [], viewport: { scrollY: 0, maxScrollY: 0, atTop: true, atBottom: true, focusedFieldId: null } };
  }

  const visibleControls: AquaVisibleControl[] = [];
  const seenControls = new Set<string>();
  for (const element of Array.from(document.querySelectorAll<HTMLElement>("[data-aqua-id]"))) {
    const controlId = element.dataset.aquaId;
    if (!controlId || seenControls.has(controlId) || !isVisible(element)) continue;
    seenControls.add(controlId);
    const rawLabel = element.dataset.aquaLabel || element.getAttribute("aria-label") || element.textContent || controlId;
    visibleControls.push({
      id: controlId,
      label: sanitizeDiagnosticText(rawLabel.replace(/\s+/g, " ").trim(), 120),
      kind: (element.dataset.aquaKind as AquaVisibleControl["kind"]) || (element.tagName === "FORM" ? "form" : "button"),
      consequence: (element.dataset.aquaConsequence as AquaControlConsequence) || "safe",
      teachingReason: element.dataset.aquaWhy ? sanitizeDiagnosticText(element.dataset.aquaWhy, 220) : undefined,
      expectedResult: element.dataset.aquaNext ? sanitizeDiagnosticText(element.dataset.aquaNext, 220) : undefined,
    });
  }

  const writableFields: AquaWritableField[] = [];
  for (const element of Array.from(document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-aqua-field]"))) {
    const fieldId = element.dataset.aquaField;
    if (!fieldId || !isVisible(element)) continue;
    writableFields.push({
      id: fieldId,
      label: sanitizeDiagnosticText(element.dataset.aquaLabel || element.getAttribute("aria-label") || fieldId, 100),
      inputType: element instanceof HTMLInputElement ? element.type : element.tagName.toLowerCase(),
      required: element.required,
    });
  }

  const scroller = activeScrollContainer();
  const scrollY = scroller.scrollTop;
  const maxScrollY = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  const focused = document.activeElement instanceof HTMLElement ? document.activeElement.dataset.aquaField ?? null : null;
  return {
    visibleControls,
    writableFields,
    viewport: {
      scrollY,
      maxScrollY,
      atTop: scrollY <= 2,
      atBottom: maxScrollY - scrollY <= 2,
      focusedFieldId: focused,
    },
  };
}

export function enrichScreenContext(context: AquaScreenContext): AquaScreenContext {
  const live = readLiveScreenState();
  return {
    ...context,
    ...live,
    registeredControls: Array.from(new Set([...context.registeredControls, ...live.visibleControls.map((control) => control.id)])),
  };
}
